/**
 * useProfileDerivations (ORCH-410 §4.3)
 *
 * Purpose: The Country-derived defaults the `user` profile form declares: each
 *          `user` Field's `default_from_fielddef_id` / `default_from_attribute`,
 *          read from the same cached edit view TemplateForm renders, so the card
 *          resolves what the form shows (Locale, Time Zone, the Phone calling
 *          code) from metadata rather than a hardcoded list.
 * Dependencies: metadataService.getModelView, utils/profileFormatting.derivedDefaults
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { metadataService } from '../../services/metadataService';
import { metadataViewQueryKey } from '../../hooks/useModelFieldLabels';
import type { ViewConfiguration } from '../metadata-form/types';
import { derivedDefaults, type DerivedDefault } from '../../utils/profileFormatting';

export const USER_MODEL_ID = 'user';

export interface ProfileDerivations {
  derivations: DerivedDefault[];
  loading: boolean;
}

export const useProfileDerivations = (tenantId: string): ProfileDerivations => {
  const { data, isLoading } = useQuery<ViewConfiguration>({
    queryKey: metadataViewQueryKey(USER_MODEL_ID, 'edit-modal', tenantId),
    queryFn: async () =>
      (await metadataService.getModelView(
        USER_MODEL_ID,
        tenantId,
        'edit-modal'
      )) as unknown as ViewConfiguration,
    staleTime: 5 * 60 * 1000,
    enabled: !!tenantId,
  });
  const derivations = useMemo(() => derivedDefaults(data?.fields), [data]);
  return { derivations, loading: isLoading };
};

export default useProfileDerivations;
